import api from './api';
import { LoginResponse, User, UserRole } from '../types';
import {
  clearAuthData,
  getAuthItem,
  getRememberMe,
  persistAuthBundle,
  setAuthItem,
} from '../utils/authStorage';
import { decodeJwtRole } from '../utils/jwtPayload';

export interface LoginDto {
  username: string;
  password: string;
  sms_code?: string;
}

export interface SmsLoginDto {
  phone: string;
  sms_code: string;
}

export interface SendSmsCodeDto {
  phone: string;
  scene: 'register' | 'login' | 'change_password';
}

export interface LoginResponseWithVerify {
  need_verify?: boolean;
  need_role_select?: boolean;
  role_select_ticket?: string;
  candidates?: LoginRoleCandidate[];
  message?: string;
  phone_masked?: string;
  access_token?: string;
  refresh_token?: string;
  session_id?: string;
  expires_in?: number;
  user?: LoginResponse['user'];
  must_change_password?: boolean;
  tenant_audit_status?: string;
}

export interface LoginRoleCandidate {
  user_id: number;
  role: string;
  role_label: string;
  org_id: number;
  org_name: string;
  real_name: string;
}

export interface RegisterDto {
  username: string;
  password: string;
  real_name: string;
  role: string;
  org_id: number;
  email?: string;
  phone?: string;
}

export interface LoginOptions {
  /** 默认 true：与历史行为一致，勾选「记住我」写入 localStorage */
  rememberMe?: boolean;
}

class AuthService {
  async sendSmsCode(data: SendSmsCodeDto): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>('/auth/send-sms-code', data);
    return response.data;
  }

  async login(
    data: LoginDto,
    options?: LoginOptions,
  ): Promise<LoginResponse | LoginResponseWithVerify> {
    const response = await api.post<LoginResponse & LoginResponseWithVerify>('/auth/login', {
      username: data.username,
      password: data.password,
      ...(data.sms_code ? { sms_code: data.sms_code } : {}),
    });
    const d = response.data;
    if (d.access_token && d.user) {
      this.persistAuth(d, options?.rememberMe !== false);
    }
    return d;
  }

  async loginBySms(
    data: SmsLoginDto,
    options?: LoginOptions,
  ): Promise<LoginResponseWithVerify> {
    const response = await api.post<LoginResponseWithVerify>('/auth/login-by-sms', {
      phone: data.phone,
      sms_code: data.sms_code,
    });
    const d = response.data;
    if (d.access_token && d.user) {
      this.persistAuth(d, options?.rememberMe !== false);
    }
    return d;
  }

  async selectLoginRole(
    data: { role_select_ticket: string; user_id: number },
    options?: LoginOptions,
  ): Promise<LoginResponseWithVerify> {
    const response = await api.post<LoginResponseWithVerify>('/auth/select-login-role', data);
    const d = response.data;
    if (d.access_token && d.user) {
      this.persistAuth(d, options?.rememberMe !== false);
    }
    return d;
  }

  /** 管理平台 ↔ 严选运营：同号可切换身份 */
  async listSwitchableWorkbenchRoles(): Promise<{
    candidates: Array<{
      user_id: number;
      role: string;
      role_label: string;
      org_id: number;
      org_name: string;
      real_name: string;
    }>;
  }> {
    const response = await api.get('/auth/switchable-workbench-roles');
    return response.data;
  }

  /** 会话内切换身份（免验证码），沿用当前「记住我」 */
  async switchLoginRole(userId: number): Promise<LoginResponseWithVerify> {
    const response = await api.post<LoginResponseWithVerify>('/auth/switch-login-role', {
      user_id: userId,
    });
    const d = response.data;
    if (d.access_token && d.user) {
      this.persistAuth(d, getRememberMe());
    }
    return d;
  }

  private persistAuth(d: LoginResponseWithVerify | LoginResponse, rememberMe: boolean) {
    if (!d.access_token || !d.user) return;
    const extended = d as LoginResponseWithVerify;
    const user = {
      ...d.user,
      must_change_password:
        extended.must_change_password ?? d.user.must_change_password ?? false,
    };
    persistAuthBundle(
      {
        access_token: d.access_token,
        refresh_token: extended.refresh_token,
        session_id: extended.session_id,
        user: JSON.stringify(user),
      },
      rememberMe,
    );
  }

  async refreshToken(refreshToken?: string) {
    const token = refreshToken || getAuthItem('refresh_token');
    if (!token) return null;
    const response = await api.post<LoginResponseWithVerify>('/auth/refresh', {
      refresh_token: token,
    });
    const d = response.data;
    if (d.access_token) setAuthItem('access_token', d.access_token);
    if (d.refresh_token) setAuthItem('refresh_token', d.refresh_token);
    if (d.session_id) setAuthItem('session_id', d.session_id);
    return d;
  }

  async getSessions() {
    const response = await api.get<Array<any>>('/auth/sessions');
    return response.data;
  }

  async revokeSession(sessionId: string) {
    const response = await api.delete(`/auth/sessions/${sessionId}`);
    return response.data;
  }

  async revokeAllSessions() {
    const response = await api.post('/auth/sessions/revoke-all');
    return response.data;
  }

  async sendChangePasswordSmsCode(): Promise<{ success: boolean; message: string }> {
    const response = await api.post<{ success: boolean; message: string }>(
      '/users/change-password/send-sms-code',
    );
    return response.data;
  }

  async changePassword(
    old_password: string | undefined,
    new_password: string,
    options?: { requireOldPassword?: boolean; sms_code?: string },
  ) {
    const payload: { new_password: string; old_password?: string; sms_code?: string } = {
      new_password,
    };
    if (old_password) {
      payload.old_password = old_password;
    } else if (options?.requireOldPassword !== false) {
      throw new Error('请输入原密码');
    }
    if (options?.sms_code) {
      payload.sms_code = options.sms_code;
    }
    const response = await api.post<{ message: string; must_change_password?: boolean }>(
      '/users/change-password',
      payload,
    );
    return response.data;
  }

  async register(data: RegisterDto): Promise<User> {
    const response = await api.post<User>('/auth/register', data);
    return response.data;
  }

  logout() {
    const session_id = getAuthItem('session_id');
    if (session_id) {
      api.post('/auth/logout', { session_id }).catch(() => undefined);
    }
    clearAuthData();
  }

  getCurrentUser(): User | null {
    const userStr = getAuthItem('user');
    if (userStr) {
      try {
        return JSON.parse(userStr);
      } catch {
        return null;
      }
    }
    return null;
  }

  async fetchCurrentUser(): Promise<User | null> {
    if (!this.isAuthenticated()) return null;
    try {
      const response = await api.get<any>('/auth/me');
      const u = response.data;
      const user: User = {
        id: u.id,
        username: u.username,
        real_name: u.real_name || '',
        role: u.role as any,
        org_id: u.org_id,
        email: u.email || undefined,
        phone: u.phone || undefined,
        organization: u.organization ? { id: u.organization.id, name: u.organization.name } : undefined,
        is_active: u.is_active ?? true,
        created_at: u.created_at || '',
        updated_at: u.updated_at || '',
        last_login_at: u.last_login_at || null,
        must_change_password: !!u.must_change_password,
      };
      setAuthItem('user', JSON.stringify(user));
      return user;
    } catch {
      const cached = this.getCurrentUser();
      const jwtRole = decodeJwtRole(getAuthItem('access_token'));
      if (cached && jwtRole && cached.role !== jwtRole) {
        const reconciled = { ...cached, role: jwtRole as UserRole };
        setAuthItem('user', JSON.stringify(reconciled));
        return reconciled;
      }
      return cached;
    }
  }

  isAuthenticated(): boolean {
    return !!getAuthItem('access_token');
  }

  isRememberMe(): boolean {
    return getRememberMe();
  }
}

const authService = new AuthService();

export default authService;
